import type { JSX } from 'react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { sessionInfoSelectors } from '@leanpay/models'
import { cn } from '@leanpay/ui'

import { useGetSessionInfo } from 'library/hooks/query-hooks'

// Types
interface ProgressPage {
    route: string
    progress: number
}

interface ProgressStep {
    id: string
    label: string
    pages: ProgressPage[]
}

type StepStatus = 'completed' | 'active' | 'inactive'

// Constants
const STEP_IDS = {
    TERMS_AND_LOGIN: 'terms-and-login',
    REGISTRATION: 'registration',
    PURCHASE_LIMIT_INFO: 'purchase-limit-info',
} as const

// Types for localization
interface StepLocalizations {
    termsAndLogin: string
    registration: string
    purchaseLimitInfo: string
}

// Step configurations
const getStepConfigurations = (localizations: StepLocalizations) => {
    const firstTimeShopperSteps: ProgressStep[] = [
        {
            id: STEP_IDS.TERMS_AND_LOGIN,
            label: localizations.termsAndLogin,
            pages: [
                { route: '/check-limit', progress: 100 },
                { route: '/request-otp', progress: 100 },
                { route: '/login', progress: 100 },
                { route: '/update-user-info', progress: 100 },
            ],
        },
        {
            id: STEP_IDS.REGISTRATION,
            label: localizations.registration,
            pages: [
                { route: '/personal-details', progress: 25 },
                { route: '/two-fa-pin', progress: 50 },
                { route: '/financial-details', progress: 75 },
                { route: '/preview-financial-details', progress: 100 },
            ],
        },
        {
            id: STEP_IDS.PURCHASE_LIMIT_INFO,
            label: localizations.purchaseLimitInfo,
            pages: [
                { route: '/validate-offer', progress: 50 },
                { route: '/prequalified-success', progress: 100 },
                { route: '/prequalified-error', progress: 100 },
            ],
        },
    ]

    const returningShopperSteps: ProgressStep[] = [
        {
            id: STEP_IDS.TERMS_AND_LOGIN,
            label: localizations.termsAndLogin,
            pages: [
                { route: '/check-limit', progress: 50 },
                { route: '/request-otp', progress: 75 },
                { route: '/login', progress: 100 },
                { route: '/update-user-info', progress: 100 },
            ],
        },
        {
            id: STEP_IDS.PURCHASE_LIMIT_INFO,
            label: localizations.purchaseLimitInfo,
            pages: [
                { route: '/validate-offer', progress: 50 },
                { route: '/prequalified-success', progress: 100 },
                { route: '/prequalified-error', progress: 100 },
            ],
        },
    ]

    return {
        firstTimeShopperSteps,
        returningShopperSteps,
    }
}

// Component helpers
const StepContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2 flex-1">{children}</div>
)

const StepBar = ({ status }: { status: 'completed' | 'inactive' }) => (
    <div
        className={cn(
            'h-1 rounded-full flex-1',
            status === 'completed' ? 'bg-lp-red' : 'bg-lp-dark-label',
        )}
    />
)

const StepText = ({
    status,
    children,
    className,
}: {
    status: StepStatus
    children: React.ReactNode
    className?: string
}) => (
    <p
        className={cn(
            'text-xs font-bold mb-0',
            status === 'completed' && 'text-lp-red',
            status === 'active' && 'text-lp-red',
            status === 'inactive' && 'text-lp-dark-label',
            className,
        )}
    >
        {children}
    </p>
)

// Helper functions
const findCurrentPage = (
    pages: ProgressPage[],
    location: ReturnType<typeof useLocation>,
): ProgressPage | null => {
    const currentPath = location.pathname
    const currentSearch = location.search
    const fullPath = currentPath + currentSearch

    return (
        pages.find((page) => {
            if (page.route.includes('?')) {
                return fullPath === page.route
            } else {
                return currentPath === page.route
            }
        }) ?? null
    )
}

const useProgressCalculation = (
    steps: ProgressStep[],
    location: ReturnType<typeof useLocation>,
) => {
    const getCurrentStepIndex = (): number => {
        return steps.findIndex((step) => findCurrentPage(step.pages, location) !== null)
    }

    const calculateProgressInStep = (stepIndex: number, currentStepIndex: number): number => {
        if (currentStepIndex !== stepIndex) return 0

        const currentStep = steps[stepIndex]
        const currentPage = findCurrentPage(currentStep.pages, location)

        return currentPage?.progress ?? 0
    }

    const getStepStatus = (stepIndex: number, currentStepIndex: number): StepStatus => {
        if (currentStepIndex === -1) return 'inactive'
        if (stepIndex < currentStepIndex) return 'completed'
        if (stepIndex === currentStepIndex) return 'active'
        return 'inactive'
    }

    return {
        getCurrentStepIndex,
        calculateProgressInStep,
        getStepStatus,
    }
}

const ProgressStepBar = ({
    status,
    progressPercentage,
}: {
    status: StepStatus
    progressPercentage: number
}) => {
    if (status === 'active') {
        return (
            <div
                className="h-1 rounded-full flex-1"
                style={{
                    background: `linear-gradient(to right, #E82E2E ${progressPercentage}%, #666666 ${progressPercentage}%)`,
                }}
            />
        )
    }

    return <StepBar status={status} />
}

export const EnhancedProgressBarSlo = (): JSX.Element | null => {
    const { t } = useTranslation()
    const location = useLocation()
    const { data: sessionInfo } = useGetSessionInfo()

    // Save the initial isFirstTimeShopper value to prevent changes during the session
    const isFirstTimeShopperRef = useRef<boolean | null>(null)

    // Set the value once when sessionInfo is first received
    // eslint-disable-next-line
    if (sessionInfo !== undefined && isFirstTimeShopperRef.current === null) {
        isFirstTimeShopperRef.current = sessionInfoSelectors.isFirstTimeShopper(sessionInfo)
    }

    const stepConfigurations = getStepConfigurations({
        termsAndLogin: t('common.termsAndLogin'),
        registration: t('common.registration'),
        purchaseLimitInfo: t('common.purchaseLimitInfo'),
    })

    // eslint-disable-next-line
    const steps = isFirstTimeShopperRef.current
        ? stepConfigurations.firstTimeShopperSteps
        : stepConfigurations.returningShopperSteps

    const { getCurrentStepIndex, calculateProgressInStep, getStepStatus } = useProgressCalculation(
        steps,
        location,
    )

    const currentStepIndex = getCurrentStepIndex()
    const currentStep = currentStepIndex >= 0 ? steps[currentStepIndex] : null

    return (
        <div className="w-full mt-[2px] flex flex-col">
            {/* Mobile: Show only current step text */}
            <StepText status="active" className="sm:hidden">
                {currentStep?.label}
            </StepText>

            {/* Desktop: Show all step labels above progress bars */}
            <div className="justify-between items-center hidden sm:flex gap-2">
                {steps.map((step, index) => {
                    const stepStatus = getStepStatus(index, currentStepIndex)
                    return (
                        <div className="flex flex-1" key={`${step.id}-label`}>
                            <StepText status={stepStatus}>{step.label}</StepText>
                        </div>
                    )
                })}
            </div>

            <div className="flex items-center gap-2">
                {steps.map((step, index) => {
                    const stepStatus = getStepStatus(index, currentStepIndex)
                    const progressPercentage = calculateProgressInStep(index, currentStepIndex)

                    return (
                        <StepContainer key={step.id}>
                            <ProgressStepBar
                                status={stepStatus}
                                progressPercentage={progressPercentage}
                            />
                        </StepContainer>
                    )
                })}
            </div>
        </div>
    )
}
